/**
 * Treppan Academy - Dashboard
 * Main dashboard showing enrolled courses, progress, and certificates
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, BookOpen, Award, Clock, ChevronRight, 
  Play, Trophy, Target, Sparkles, ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Design system colors
const colors = {
  background: '#0A0D14',
  surface: '#131825',
  surfaceHover: '#1C2438',
  gold: '#D4AF37',
  goldHover: '#B5952F',
  success: '#10B981',
  fail: '#EF4444',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color = colors.gold }) => (
  <div 
    className="p-6 rounded-xl transition-all duration-300 hover:-translate-y-1"
    style={{ 
      backgroundColor: colors.surface,
      border: `1px solid ${colors.border}`
    }}
  >
    <div className="flex items-center gap-4">
      <div 
        className="p-3 rounded-xl"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-6 w-6" style={{ color }} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>
          {label}
        </p>
        <p className="text-3xl font-semibold mt-1" style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}>
          {value}
        </p>
      </div>
    </div>
  </div>
);

// Course Card Component
const CourseCard = ({ course, onClick }) => {
  const progress = course.user_progress?.progress_percent || 0;
  const status = course.user_progress?.status || 'not_started';
  
  return (
    <div 
      className="rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
      style={{ 
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`
      }}
      onClick={onClick}
      data-testid={`course-card-${course.id}`}
    >
      {/* Thumbnail */}
      <div className="relative h-40 overflow-hidden">
        <img 
          src={course.thumbnail_url || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800'} 
          alt={course.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        
        {/* Difficulty Badge */}
        <Badge 
          className="absolute top-3 right-3 uppercase text-xs tracking-widest"
          style={{ 
            backgroundColor: course.difficulty === 'advanced' ? `${colors.gold}20` : `${colors.success}20`,
            color: course.difficulty === 'advanced' ? colors.gold : colors.success,
            border: `1px solid ${course.difficulty === 'advanced' ? colors.gold : colors.success}30`
          }}
        >
          {course.difficulty}
        </Badge>
        
        {/* Progress */}
        {status !== 'not_started' && (
          <div className="absolute bottom-3 left-3 right-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs" style={{ color: colors.textBody }}>{progress}% complete</span>
            </div>
            <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${progress}%`,
                  backgroundColor: status === 'completed' ? colors.success : colors.gold
                }}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-5">
        <h3 
          className="text-lg font-semibold mb-2 line-clamp-1"
          style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
        >
          {course.title}
        </h3>
        <p className="text-sm line-clamp-2 mb-4" style={{ color: colors.textBody }}>
          {course.description}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm" style={{ color: colors.textMuted }}>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {course.duration_hours}h
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="h-4 w-4" />
              {course.lessons?.length || 0} lessons
            </span>
          </div>
          
          {status === 'completed' && (
            <Badge 
              className="uppercase text-xs tracking-widest"
              style={{ 
                backgroundColor: `${colors.success}20`,
                color: colors.success,
                border: `1px solid ${colors.success}30`
              }}
            >
              <Trophy className="h-3 w-3 mr-1" />
              Completed
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

// Certificate Card Component
const CertificateCard = ({ certificate, onClick }) => {
  const isDistinction = certificate.grade === 'distinction';
  const accentColor = isDistinction ? colors.gold : colors.fail;
  
  return (
    <div 
      className="p-4 rounded-xl cursor-pointer transition-all duration-300 hover:-translate-y-1"
      style={{ 
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`
      }}
      onClick={onClick}
      data-testid={`certificate-card-${certificate.id}`}
    >
      <div className="flex items-center gap-4">
        <div 
          className="p-3 rounded-xl"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <Award className="h-6 w-6" style={{ color: accentColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <p 
            className="font-medium truncate"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            {certificate.course_title}
          </p>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            {certificate.score}% • {isDistinction ? 'Distinction' : 'Pass'}
          </p>
        </div>
        <ChevronRight className="h-5 w-5" style={{ color: colors.textMuted }} />
      </div>
    </div>
  );
};

export default function AcademyDashboard() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academy/dashboard?token=${token}`);
      const result = await res.json();
      
      if (result.success) {
        setData(result.data);
      } else {
        toast.error('Failed to load dashboard');
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <GraduationCap className="h-12 w-12 mx-auto mb-4 animate-pulse" style={{ color: colors.gold }} />
          <p style={{ color: colors.textBody }}>Loading Academy...</p>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const continueLearning = data?.continue_learning || [];
  const enrolledCourses = data?.enrolled_courses || [];
  const recommendedCourses = data?.recommended_courses || [];
  const certificates = data?.certificates || [];

  return (
    <div className="min-h-screen p-6 lg:p-8" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div 
            className="p-2 rounded-xl"
            style={{ backgroundColor: `${colors.gold}15` }}
          >
            <GraduationCap className="h-6 w-6" style={{ color: colors.gold }} />
          </div>
          <h1 
            className="text-3xl font-bold tracking-tight"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            Treppan Academy
          </h1>
        </div>
        <p style={{ color: colors.textBody }}>
          Master Treppan CRM and earn certifications
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          icon={BookOpen} 
          label="Enrolled Courses" 
          value={stats.enrolled_courses || 0}
          color={colors.gold}
        />
        <StatCard 
          icon={Target} 
          label="In Progress" 
          value={stats.in_progress || 0}
          color="#3B82F6"
        />
        <StatCard 
          icon={Trophy} 
          label="Completed" 
          value={stats.completed_courses || 0}
          color={colors.success}
        />
        <StatCard 
          icon={Award} 
          label="Certificates" 
          value={stats.certificates_earned || 0}
          color="#A855F7"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Continue Learning - Large Hero */}
        <div className="lg:col-span-8">
          {continueLearning.length > 0 ? (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 
                  className="text-xl font-semibold tracking-tight"
                  style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
                >
                  Continue Learning
                </h2>
              </div>
              
              {/* Featured Course */}
              <div 
                className="rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl"
                style={{ 
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  boxShadow: `0 8px 32px rgba(212, 175, 55, 0.1)`
                }}
                onClick={() => navigate(`/academy/courses/${continueLearning[0].id}`)}
                data-testid="continue-learning-hero"
              >
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="relative h-48 md:h-64">
                    <img 
                      src={continueLearning[0].thumbnail_url || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800'}
                      alt={continueLearning[0].title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 flex flex-col justify-center">
                    <Badge 
                      className="w-fit mb-3 uppercase text-xs tracking-widest"
                      style={{ 
                        backgroundColor: `${colors.gold}20`,
                        color: colors.gold,
                        border: `1px solid ${colors.gold}30`
                      }}
                    >
                      <Sparkles className="h-3 w-3 mr-1" />
                      In Progress
                    </Badge>
                    <h3 
                      className="text-2xl font-bold mb-2"
                      style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
                    >
                      {continueLearning[0].title}
                    </h3>
                    <p className="mb-4 line-clamp-2" style={{ color: colors.textBody }}>
                      {continueLearning[0].description}
                    </p>
                    
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm" style={{ color: colors.textMuted }}>
                          {continueLearning[0].progress?.progress_percent || 0}% complete
                        </span>
                      </div>
                      <Progress 
                        value={continueLearning[0].progress?.progress_percent || 0}
                        className="h-2"
                        style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                      />
                    </div>
                    
                    <Button 
                      className="w-fit rounded-full font-semibold"
                      style={{ backgroundColor: colors.gold, color: '#000' }}
                      data-testid="continue-course-btn"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Continue Course
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div 
              className="rounded-xl p-8 text-center mb-8"
              style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
            >
              <GraduationCap className="h-12 w-12 mx-auto mb-4" style={{ color: colors.textMuted }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: colors.textHeading }}>
                Start Your Learning Journey
              </h3>
              <p className="mb-4" style={{ color: colors.textBody }}>
                Browse our courses and start learning today
              </p>
              <Button 
                onClick={() => navigate('/academy/courses')}
                className="rounded-full font-semibold"
                style={{ backgroundColor: colors.gold, color: '#000' }}
                data-testid="browse-courses-btn"
              >
                Browse Courses
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Enrolled Courses */}
          {enrolledCourses.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 
                  className="text-xl font-semibold tracking-tight"
                  style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
                >
                  My Courses
                </h2>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/academy/courses')}
                  className="text-sm"
                  style={{ color: colors.gold }}
                >
                  View All
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {enrolledCourses.slice(0, 4).map(course => (
                  <CourseCard 
                    key={course.id} 
                    course={course}
                    onClick={() => navigate(`/academy/courses/${course.id}`)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-6">
          {/* Certificates */}
          <div 
            className="rounded-xl p-6"
            style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 
                className="font-semibold"
                style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
              >
                My Certificates
              </h3>
              {certificates.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/academy/certificates')}
                  style={{ color: colors.gold }}
                >
                  View All
                </Button>
              )}
            </div>
            
            {certificates.length > 0 ? (
              <div className="space-y-3">
                {certificates.slice(0, 3).map(cert => (
                  <CertificateCard 
                    key={cert.id}
                    certificate={cert}
                    onClick={() => navigate(`/academy/certificates/${cert.id}`)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Award className="h-10 w-10 mx-auto mb-3" style={{ color: colors.textMuted }} />
                <p className="text-sm" style={{ color: colors.textMuted }}>
                  Complete courses to earn certificates
                </p>
              </div>
            )}
          </div>

          {/* Recommended Courses */}
          {recommendedCourses.length > 0 && (
            <div 
              className="rounded-xl p-6"
              style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5" style={{ color: colors.gold }} />
                <h3 
                  className="font-semibold"
                  style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
                >
                  Recommended for You
                </h3>
              </div>
              <div className="space-y-4">
                {recommendedCourses.slice(0, 3).map(course => (
                  <div 
                    key={course.id}
                    className="flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-all hover:bg-white/5"
                    onClick={() => navigate(`/academy/courses/${course.id}`)}
                    data-testid={`recommended-${course.id}`}
                  >
                    <img 
                      src={course.thumbnail_url || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=200'}
                      alt={course.title}
                      className="w-16 h-12 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p 
                        className="font-medium text-sm truncate"
                        style={{ color: colors.textHeading }}
                      >
                        {course.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs" style={{ color: colors.textMuted }}>
                          {course.duration_hours}h • {course.difficulty}
                        </p>
                        {course._recommendation_reason && (
                          <>
                            <span style={{ color: colors.textMuted }}>•</span>
                            <p className="text-xs" style={{ color: colors.gold }}>
                              {course._recommendation_reason}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
